/** The router: main menu -> new game -> adventure, with every full-screen panel
 *  laid over it.
 *
 * The app model is an external store, so this component is a projection of it
 * and holds nothing but the two asset handles, the player's preferences and
 * which system panel is up.
 *
 * Three things here are load-bearing and were not, before:
 *
 *  - **An error is a dialog, never a replacement.** `snapshot.error` used to be
 *    rendered as a div *instead of the whole application*, so one failed Load
 *    Game turned the window into a single sentence with no buttons on it and
 *    only a browser reload recovered. An error is now a dismissible dialog over
 *    whatever was on screen. The one exception is an error raised before there
 *    is anything to draw at all - no atlas, no content - which is still full
 *    screen because there is genuinely nothing behind it.
 *
 *  - **The autosave is written here.** `saveToBrowser` had zero callers, so the
 *    "autosave" slot never existed, so Load Game could not succeed on a clean
 *    profile. It is written at the top of each of the player's turns, which is
 *    the only thing that makes Load Game mean anything.
 *
 *  - **Every plate on the title screen does something**, and there is a way out
 *    of a running game: the system panel over the adventure screen (Escape, F10
 *    or the plate in the corner) is where Save, Load, Settings and the road back
 *    to the menu live.
 */

import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { BattleCommand, ContentRegistry, GameEvent } from "@game/core";
import { FULL_PRICE_BP, activeStack, executeBattleCommand, reasonText, upgradeOffers } from "@game/core";
import type { Atlas, BattlePresenter } from "@game/render";
import { ensureGpuAtlas } from "./game/gpuAtlas.js";
import type { Atlas as UiAtlas } from "@game/ui";
import {
  AtlasContext, AudioProvider, DEFAULT_VOLUMES, EMPTY_ATLAS, GameTheme, RotateHint, ScreenDialog,
  ScreenStyles, createAtlas, nearestStep,
} from "@game/ui";
import type { AtlasManifest } from "@game/ui";
import {
  BattleResult, CreditsScreen, DEFAULT_SETTINGS, HandOff, HeroScreen, HighScores,
  MainMenu, MAP_SIZES, NewGameScreen, SaveLoad, Settings, SpellBook, TownScreen,
} from "@game/ui";
import type { HighScoreRow, MainMenuAction, MainMenuItem, SettingsValues } from "@game/ui";
import { OfflineCard } from "./OfflineCard.js";
import { assetDir } from "./base.js";
import { describeBuildingBuilt, heroPortrait } from "@game/ui";
import { AdventureShell } from "./AdventureShell.js";
import { GameAudio } from "./game/audio.js";
import { EditorRoute } from "./editor/EditorRoute.js";
import { CampaignRoute } from "./campaign/CampaignRoute.js";
import { BattleScreen } from "./battle/BattleScreen.js";
import { app } from "./game/app.js";
import type { GameSession } from "./game/store.js";
import { journalNames, viewerNews } from "./game/journal.js";
import { loadContentBundle } from "./game/content.js";
import { armyFixture, battleFixture, heroFixture, townFixture } from "./game/fixtures.js";
import { DEFAULT_OPTIONS } from "./game/newgame.js";
import { AUTOSAVE_SLOT, deleteSlot, rememberedOptions, type SlotInfo } from "./game/persist.js";
import { clearScores, readScores, recordScore, scoreRow } from "./game/scores.js";
import { readProgress } from "./game/campaign.js";

const ASSETS = assetDir("/assets");

/** The build stamp shown on the title screen's foot rail and in the credits. */
const VERSION = "Phase B";

/** True only while the viewer is a side in the running battle. */
function viewerIsInBattle(session: GameSession | null): boolean {
  const b = session?.getState().battle ?? null;
  if (b === null || session === null) return false;
  const me = session.getViewer();
  return b.heroes[0].owner === me || b.heroes[1].owner === me;
}

/** `useSyncExternalStore` must be handed a real unsubscribe even when there is
 *  nothing to subscribe to. */
const NO_UNSUBSCRIBE = (): void => { /* nothing was subscribed */ };

interface Assets {
  readonly ui: UiAtlas | null;
  readonly pixi: Atlas | null;
  readonly error: string | null;
  readonly dismissError: () => void;
}

/**
 * The DOM panels need only the manifest; the Pixi screens need the GPU atlas.
 * There is exactly one of it for the whole app (`game/gpuAtlas.ts`) - the
 * adventure presenter borrows the same instance - and it is built only when a
 * screen that draws from it is about to mount.
 */

function useAssets(wantGpu: boolean): Assets {
  const [ui, setUi] = useState<UiAtlas | null>(null);
  const [pixi, setPixi] = useState<Atlas | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const load = async (): Promise<void> => {
      const res = await fetch(`${ASSETS}/assets.json`);
      if (!res.ok) throw new Error(`assets.json: HTTP ${String(res.status)}`);
      const manifest = (await res.json()) as AtlasManifest;
      if (live) setUi(createAtlas(manifest, ASSETS));
    };
    void load().catch((e: unknown) => {
      if (live) setError(e instanceof Error ? e.message : String(e));
    });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!wantGpu) return;
    let live = true;
    const loading = ensureGpuAtlas();
    // Registered before the promise settles: `settle()` must not photograph the
    // "preparing scene" placeholder while thirteen sheets are still decoding.
    const drop = app.addSettler("gpu-atlas", async () => { await loading.catch(() => undefined); });
    void loading
      .then((a) => { if (live) setPixi(a); })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      });
    return () => { live = false; drop(); };
  }, [wantGpu]);

  const dismissError = useCallback(() => { setError(null); }, []);
  return { ui, pixi, error, dismissError };
}

// ------------------------------------------------------------- preferences

const SETTINGS_KEY = "realm.settings.v1";

/** Preferences are a host concern, like the two save slots: the kernel has no
 *  clock and no storage, and none of this reaches a `GameState`. */
function readSettings(): SettingsValues {
  try {
    const raw = globalThis.localStorage.getItem(SETTINGS_KEY);
    if (raw === null) return DEFAULT_SETTINGS;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return DEFAULT_SETTINGS;
    const o = parsed as Partial<SettingsValues>;
    const scale = typeof o.panelScale === "number" && o.panelScale > 0 ? o.panelScale : null;
    return {
      animations: o.animations !== false,
      autosave: o.autosave !== false,
      panelScale: scale,
      volumes: readVolumes(o.volumes),
    };
  } catch {
    // A private window, a browser set to block site data, or a half-written
    // value. None of those is a reason to refuse to start.
    return DEFAULT_SETTINGS;
  }
}

/** A volume that came out of storage. Anything not a number in 0..1 is the
 *  default, and everything is snapped back to a step the screen can show as
 *  pressed - a half-written value must not leave three buttons unlit. */
function readVolumes(v: SettingsValues["volumes"] | undefined): SettingsValues["volumes"] {
  if (v === null || typeof v !== "object") return DEFAULT_VOLUMES;
  const one = (x: unknown, fallback: number): number =>
    (typeof x === "number" && x >= 0 && x <= 1 ? nearestStep(x) : fallback);
  return {
    master: one(v.master, DEFAULT_VOLUMES.master),
    sfx: one(v.sfx, DEFAULT_VOLUMES.sfx),
    music: one(v.music, DEFAULT_VOLUMES.music),
  };
}

function writeSettings(values: SettingsValues): void {
  try {
    globalThis.localStorage.setItem(SETTINGS_KEY, JSON.stringify(values));
  } catch { /* a preference that will not persist is not worth failing over */ }
}

// ------------------------------------------------------------------- panels

/** The system layer: what sits over whatever screen is up. */
type Panel = "system" | "save" | "load" | "settings" | "credits" | "scores" | null;

/** A question that has to be answered before anything is thrown away.
 *
 * `leave` and `abandon` are deliberately two different acts. Stepping out of a
 * game to the title screen costs nothing - `app.go({kind:"menu"})` does not
 * touch the session, so Return to Game brings the whole thing back - and that
 * is what the in-game exit does. Throwing the game away is a separate plate on
 * the title screen with its own question, because it is the irreversible one. */
type Confirm = "leave" | "abandon" | "close-tab" | null;

/** The bindings this component owns, for the settings screen's reference card. */
const SYSTEM_KEYS: readonly { readonly combo: string; readonly description: string }[] =
  Object.freeze([
    { combo: "Esc", description: "Open the system menu, or close what is open" },
    { combo: "F10", description: "Open the system menu" },
    { combo: "F5", description: "Quicksave" },
    { combo: "F9", description: "Load the quicksave" },
    { combo: "Space", description: "Next hero with movement left" },
    { combo: "E", description: "End turn" },
    { combo: "C", description: "Centre on the selected hero" },
    { combo: "H", description: "The selected hero's sheet" },
    { combo: "T", description: "The selected town" },
    { combo: "K", description: "Kingdom overview" },
    { combo: "J", description: "Journal: tasks and what has happened" },
    { combo: "Arrows", description: "Scroll the map" },
  ]);

export function App(): JSX.Element {
  const snapshot = useSyncExternalStore(app.subscribe, app.getSnapshot);
  const session = snapshot.session;

  // Two stores, and this component needs both.
  //
  // A battle starts inside the turn loop, which writes through the session and
  // bumps ITS version; the app store is not one of the session's listeners and
  // `app.dispatch` hands a human's command to the controller and returns before
  // it emits. So subscribing to the app store alone leaves `wantGpu` false for
  // the whole fight: the atlas is never asked for, `assets.pixi` stays null,
  // and the shell's battle branch - which requires it - is never taken. What
  // the player sees is a frozen map with a battle in state and the loop parked
  // on him forever. The fix is to read "am I in a battle" from the authority
  // that knows, through its own subscription.
  const inBattle = useSyncExternalStore(
    useCallback((l: () => void) => (session === null ? NO_UNSUBSCRIBE : session.subscribe(l)), [session]),
    useCallback(() => viewerIsInBattle(session), [session]),
  );

  // The autosave fires on the day, not on the app store's revision: with no
  // opponents the actor never changes hands, so "the actor became human again"
  // is not a turn boundary and the day is.
  const day = useSyncExternalStore(
    useCallback((l: () => void) => (session === null ? NO_UNSUBSCRIBE : session.subscribe(l)), [session]),
    useCallback(() => session?.getState().turn.day ?? 0, [session]),
  );

  // A battle screen and the battle fixture are the only things that take the
  // GPU atlas as a prop; everything else on the adventure screen goes through
  // the presenter's own. A battle between a computer player and a wandering
  // monster is not shown, so it must not pull the atlas onto the GPU either.
  const wantGpu = snapshot.fixture?.kind === "battle" || inBattle;
  const assets = useAssets(wantGpu);

  const [settings, setSettings] = useState<SettingsValues>(readSettings);
  const [panel, setPanel] = useState<Panel>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [slots, setSlots] = useState<readonly SlotInfo[]>(() => app.savedGames());
  /** The last save or load outcome, in the persistence layer's own words. */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * The seat waiting to be taken, and what the hand-off screen needs to name
   * it. Read off the snapshot rather than the session, because the app model
   * is what decides a hand-off is owed and it emits when that changes.
   */
  const handoff = snapshot.handoff;
  const handoffColour = handoff === null
    ? "red"
    : session?.getState().players[handoff]?.colour ?? "red";
  const handoffSeats = useMemo(() => {
    if (handoff === null || session === null) return undefined;
    const state = session.getState();
    return app.humanSeats().map((id) => state.players[id]?.colour ?? "red");
  }, [handoff, session]);

  /** Where the player is on the road, for the menu's own line. */
  const campaignHint = useMemo(() => {
    const p = readProgress();
    return p.at === 0
      ? "Four maps, each harder than the last"
      : `Map ${String(p.at + 1)} of 4`;
  }, [snapshot.screen.kind]);

  const [scores, setScores] = useState<readonly HighScoreRow[]>(readScores);
  /** The row this session just wrote, so the table can point at it. */
  const [newestScoreAt, setNewestScoreAt] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    void loadContentBundle()
      .then((c: ContentRegistry) => { if (live) app.setContent(c); })
      .catch((e: unknown) => {
        if (live) app.setError(e instanceof Error ? e.message : String(e));
      });
    return () => { live = false; };
  }, []);

  // Applied once, at mount. The debug surface drives `setAnimationsEnabled` too
  // (a screenshot must not race an animation), so a preference *effect* keyed on
  // the value would silently switch animations back on under the harness.
  const booted = useRef(settings);
  useEffect(() => {
    if (!booted.current.animations) app.setAnimationsEnabled(false);
  }, []);

  // A game that has been decided is scored once, whoever won. It is keyed on
  // the session object rather than on a flag: `victory.over` stays true for the
  // rest of the run, and starting a new game must be scorable again.
  const gameOver = useSyncExternalStore(
    useCallback((l: () => void) => (session === null ? NO_UNSUBSCRIBE : session.subscribe(l)), [session]),
    useCallback(() => session?.getState().victory.over ?? false, [session]),
  );
  const scoredSession = useRef<GameSession | null>(null);
  useEffect(() => {
    if (session === null || !gameOver || scoredSession.current === session) return;
    const content = app.getSnapshot().content;
    if (content === null) return;
    scoredSession.current = session;
    const row = scoreRow(
      session.getState(), content, session.getViewer(), app.getSnapshot().options,
    );
    setScores(recordScore(row));
    setNewestScoreAt(row.at);
  }, [session, gameOver]);

  const changeSettings = useCallback((next: SettingsValues): void => {
    setSettings(next);
    writeSettings(next);
    app.setAnimationsEnabled(next.animations);
  }, []);

  const error = snapshot.error ?? assets.error;
  // The model has no "clear"; an empty message is the absence of one, and this
  // component is the only reader of the field.
  const showsError = error !== null && error !== "";
  const dismissError = useCallback((): void => {
    if (app.getSnapshot().error !== null) app.setError("");
    assets.dismissError();
  }, [assets]);

  const refreshSlots = useCallback((): readonly SlotInfo[] => {
    const rows = app.savedGames();
    setSlots(rows);
    return rows;
  }, []);

  // ---------------------------------------------------------------- autosave

  /** The (session, day) the autosave was last written for. A new session starts
   *  a new run of days, so the session identity is half the key. */
  const savedFor = useRef<{ session: GameSession | null; day: number }>({ session: null, day: -1 });
  useEffect(() => {
    if (!settings.autosave) return;
    if (session === null || snapshot.actorKind !== "human") return;
    const at = savedFor.current;
    if (at.session === session && at.day === day) return;
    savedFor.current = { session, day };

    const failed = app.saveToBrowser(AUTOSAVE_SLOT);
    refreshSlots();
    if (failed === null) return;
    // A quota failure repeats every single turn. It must not seize the screen
    // once, let alone thirty times; it is reported where saving is discussed.
    app.setError("");
    setNotice(`The autosave could not be written. ${failed}`);
  }, [session, day, snapshot.actorKind, settings.autosave, refreshSlots]);

  // A building finished while the player looked elsewhere - by hand in a town
  // screen that has since closed, or by the governor overnight. The journal
  // keeps the record; the toast says it once, in the same words. Keyed on the
  // session: a loaded game starts a fresh log, so there is nothing old to say.
  const builtSeen = useRef<{ session: GameSession | null; count: number }>({ session: null, count: 0 });
  const builtCount = useSyncExternalStore(
    useCallback((l: () => void) => (session === null ? NO_UNSUBSCRIBE : session.subscribe(l)), [session]),
    useCallback(() => session?.eventLog().length ?? 0, [session]),
  );
  useEffect(() => {
    if (session === null || snapshot.content === null) return;
    const seen = builtSeen.current;
    if (seen.session !== session) {
      seen.session = session;
      seen.count = session.eventLog().length;
      return;
    }
    const log = session.eventLog();
    const fresh = log.slice(seen.count);
    seen.count = log.length;
    // The player's own towns only. The log is shared with every other kingdom
    // in the game, so an unfiltered read toasted the computer's build order -
    // three enemy buildings and none of the player's own, on the first seed
    // this was measured on. `viewerNews` is the one place that judgement lives.
    const built = viewerNews(fresh, session.getState(), session.getViewer()).filter(
      (e): e is Extract<GameEvent, { type: "BuildingBuilt" }> => e.type === "BuildingBuilt",
    );
    if (built.length === 0) return;
    // The newest one by name, and a count for the rest: a governor in each of
    // four towns finishes four buildings on the same overnight, and a toast
    // that named only the last of them was the reason the other three felt
    // like gold leaving with nothing arriving. The Journal has them all.
    const last = built[built.length - 1]!;
    const line = describeBuildingBuilt(last, snapshot.content, journalNames(session.getState(), snapshot.content));
    const rest = built.length - 1;
    setNotice(rest === 0 ? line : `${line} And ${String(rest)} more elsewhere.`);
  }, [session, snapshot.content, builtCount]);

  // --------------------------------------------------------- save and load

  const openPanel = useCallback((next: Panel): void => {
    refreshSlots();
    setNotice(null);
    setPanel(next);
  }, [refreshSlots]);

  /** What the adventure screen's system key fires. Bound once so the memoised
   *  body does not rebuild on every render. */
  const openSystemPanel = useCallback((): void => { openPanel("system"); }, [openPanel]);

  const doSave = useCallback((slot: string): void => {
    const failed = app.saveToBrowser(slot);
    refreshSlots();
    if (failed === null) { setNotice(`Saved as "${slot}".`); return; }
    // `saveToBrowser` also parks the message on the model. The save screen is
    // already showing it, so a modal over the top would say it twice.
    app.setError("");
    setNotice(failed);
  }, [refreshSlots]);

  const doLoad = useCallback((slot: string): void => {
    const failed = app.loadFromBrowser(slot);
    if (failed === null) { setPanel(null); setNotice(null); return; }
    app.setError("");
    setNotice(failed);
    refreshSlots();
  }, [refreshSlots]);

  const doLoadFile = useCallback((file: File): void => {
    void app.loadFromDisk(file).then((failed) => {
      if (failed === null) { setPanel(null); setNotice(null); return; }
      app.setError("");
      setNotice(failed);
    });
  }, []);

  const doSaveFile = useCallback((): void => {
    const failed = app.saveToDisk();
    if (failed === null) { setNotice("The save was handed to your browser."); return; }
    app.setError("");
    setNotice(failed);
  }, []);

  const doDelete = useCallback((slot: string): void => {
    deleteSlot(slot);
    refreshSlots();
    setNotice(`Deleted "${slot}".`);
    if (slot === AUTOSAVE_SLOT) savedFor.current = { session: null, day: -1 };
  }, [refreshSlots]);

  /** Step out to the title screen, keeping the game. */
  const toMainMenu = useCallback((): void => {
    app.go({ kind: "menu" });
    setConfirm(null);
    setPanel(null);
    refreshSlots();
  }, [refreshSlots]);

  /** Throw the running game away, and with it the loop and the session. */
  const abandonGame = useCallback((): void => {
    app.reset();
    app.go({ kind: "menu" });
    savedFor.current = { session: null, day: -1 };
    setConfirm(null);
    setPanel(null);
    setNotice(null);
    refreshSlots();
  }, [refreshSlots]);

  /**
   * What the New Game screen opens on: the last setup this browser started a
   * game with, falling back to the shipped defaults for anything it does not
   * hold. Re-reading storage on every mount rather than caching it, because
   * `app.newGame` writes the value and this reads it, and the screen is only
   * built when the player asks for it.
   */
  const lastSetup = useMemo(() => {
    const last = rememberedOptions() ?? {};
    const cells = last.mapCells ?? DEFAULT_OPTIONS.mapCells;
    return {
      seed: last.seed ?? DEFAULT_OPTIONS.seed,
      mapSize: (MAP_SIZES.find((m) => m.cells === cells) ?? MAP_SIZES[1])?.id ?? "M",
      faction: last.faction ?? DEFAULT_OPTIONS.faction,
      opponents: last.opponents ?? DEFAULT_OPTIONS.opponents,
      humans: last.humans ?? DEFAULT_OPTIONS.humans ?? 1,
      difficulty: last.difficulty ?? DEFAULT_OPTIONS.difficulty,
      victory: last.victory ?? DEFAULT_OPTIONS.victory ?? "defeat_all",
    };
  }, [snapshot.screen.kind]);

  const start = useCallback((setup: {
    readonly seed: number; readonly mapCells: number; readonly faction: string;
    readonly opponents: number; readonly humans?: number; readonly difficulty: number;
    readonly victory?: string;
  }): void => {
    try {
      app.newGame({
        seed: setup.seed,
        mapCells: setup.mapCells,
        faction: setup.faction,
        opponents: setup.opponents,
        humans: setup.humans ?? 1,
        difficulty: setup.difficulty,
        victory: setup.victory ?? DEFAULT_OPTIONS.victory ?? "defeat_all",
      });
      setPanel(null);
      setNotice(null);
    } catch (e: unknown) {
      app.setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const onMenu = useCallback((action: MainMenuAction): void => {
    switch (action) {
      case "new": app.go({ kind: "newgame" }); return;
      case "campaign": app.go({ kind: "campaign" }); return;
      case "resume": if (app.getSnapshot().session !== null) app.go({ kind: "adventure" }); return;
      case "load": openPanel("load"); return;
      case "scores": setScores(readScores()); openPanel("scores"); return;
      case "editor": app.go({ kind: "editor" }); return;
      case "settings": openPanel("settings"); return;
      case "credits": openPanel("credits"); return;
      case "quit": setConfirm(app.getSnapshot().session === null ? "close-tab" : "abandon"); return;
    }
  }, [openPanel]);

  // The scoreboard, settings and load sheets are panel state private to this
  // component, so the debug surface has no way to open them without a bridge.
  // Dev only: `app.debugMenu` is null in a production build and nothing reads it.
  useEffect(() => {
    if (!import.meta.env.DEV) return undefined;
    app.debugMenu = onMenu as (action: string) => void;
    app.debugClosePanel = () => { setPanel(null); };
    return () => { app.debugMenu = null; app.debugClosePanel = null; };
  }, [onMenu]);

  // ---------------------------------------------------------------- hotkeys

  const onAdventure = snapshot.screen.kind === "adventure" && session !== null
    && snapshot.fixture === null;

  useEffect(() => {
    const stop = (e: KeyboardEvent): void => {
      e.preventDefault();
      // The adventure screen binds Escape on the window too, and it is below
      // this listener rather than beside it: a system panel that is open owns
      // the key, and closing it must not also close the town screen behind it.
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.repeat) return;
      if (e.key === "Escape") {
        // The same precedence `systemDialog` draws with, so Escape always
        // closes the thing the player is actually looking at.
        if (showsError) { stop(e); dismissError(); return; }
        if (confirm !== null) { stop(e); setConfirm(null); return; }
        if (panel !== null) { stop(e); setPanel(null); return; }
        // An overlay of the adventure screen's own is that screen's to close.
        if (app.getSnapshot().overlay.kind !== "none") return;
        if (!onAdventure) return;
        stop(e);
        openPanel("system");
        return;
      }
      // F10 is the same door as the corner plate, for a player whose hand is
      // already on the keyboard. It belongs to the map and to nothing else: the
      // title screen has the same four choices painted on it.
      if (e.key === "F10" && onAdventure) {
        stop(e);
        if (panel === null) openPanel("system"); else setPanel(null);
        return;
      }
      // Quicksave and quickload. They belong to the map for the same reason
      // F10 does, and they say what happened: a save that silently did nothing
      // because storage is full is exactly the save a player reaches for.
      if (e.key === "F5" && onAdventure) {
        stop(e);
        const failed = app.quickSave();
        setSlots(app.savedGames());
        setNotice(failed ?? "Quicksaved.");
        return;
      }
      if (e.key === "F9" && onAdventure) {
        stop(e);
        const failed = app.quickLoad();
        setNotice(failed ?? "Quicksave loaded.");
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => { window.removeEventListener("keydown", onKeyDown, true); };
  }, [panel, confirm, showsError, onAdventure, openPanel, dismissError]);

  // ----------------------------------------------------------------- render

  const uiAtlas = assets.ui;
  const ready = uiAtlas !== null && snapshot.content !== null;

  const closePanel = useCallback(() => { setPanel(null); }, []);
  const closeConfirm = useCallback(() => { setConfirm(null); }, []);
  const askLeave = useCallback(() => { setPanel(null); setNotice(null); setConfirm("leave"); }, []);
  const closeTab = useCallback(() => { setConfirm(null); globalThis.close(); }, []);

  const dialog = systemDialog({
    panel,
    confirm,
    // A boot error has nothing to sit over and is drawn full screen instead.
    error: showsError && ready ? error : null,
    slots,
    autosaveOn: settings.autosave,
    openPanel,
    closePanel,
    closeConfirm,
    askLeave,
    toMainMenu,
    abandonGame,
    closeTab,
    dismissError,
  });

  const menuItems = useMemo<readonly MainMenuItem[]>(() => {
    const out: MainMenuItem[] = [];
    // `app.go({kind:"menu"})` does not tear the session down, so a player who
    // stepped out of a game must be offered the way back in before anything
    // that would throw it away.
    if (session !== null) {
      out.push({ action: "resume", label: "Return to Game", hint: "Back to the map you left" });
    }
    out.push({ action: "new", label: "New Game" });
    out.push({
      action: "campaign",
      label: "Campaign",
      hint: campaignHint,
    });
    // `hasSavedGame` reads the slot itself; `savedGames` reads the index beside
    // it. They are the same answer until they are not - an index written by an
    // older build, or storage half-cleared - and "can Load Game actually
    // succeed" is the first question, so it is asked of the file.
    const resumable = app.hasSavedGame();
    out.push({
      action: "load",
      label: "Load Game",
      hint: resumable
        ? `Pick up the autosave, or one of ${String(slots.length)} saves`
        : slots.length === 0
          ? "Nothing is saved in this browser yet - a file from disk will do"
          : `${String(slots.length)} saved`,
    });
    out.push({
      action: "scores",
      label: "High Scores",
      hint: scores.length === 0
        ? "Nothing recorded yet - finish a game"
        : `${String(scores.length)} recorded, best ${String(scores[0]?.score ?? 0)}`,
    });
    out.push({
      action: "editor",
      label: "Map Editor",
      hint: "Draw a map, or open one, and play it",
    });
    out.push({ action: "settings", label: "Settings" });
    out.push({ action: "credits", label: "Credits" });
    out.push({
      action: "quit",
      label: session === null ? "Quit" : "Abandon Game",
      hint: session === null ? "Close this tab" : "Throw this game away and come back here",
    });
    return out;
  }, [session, slots.length, scores]);

  const body = useMemo(() => {
    // An error raised before anything can be drawn is the one that has nothing
    // to sit over. Every other one is a dialog; see the header.
    if (!ready) {
      if (showsError) return <div className="hm-boot hm-boot--error">{error}</div>;
      return <div className="hm-boot">Loading…</div>;
    }
    const content = snapshot.content;
    if (content === null) return <div className="hm-boot">Loading…</div>;

    if (snapshot.fixture !== null) {
      return (
        <Fixture
          content={content}
          pixi={assets.pixi}
          fixture={snapshot.fixture}
          animations={snapshot.animationsOn}
        />
      );
    }

    if (snapshot.screen.kind === "campaign") {
      return (
        <CampaignRoute content={content} onClose={() => { app.go({ kind: "menu" }); }} />
      );
    }

    if (snapshot.screen.kind === "editor") {
      return (
        <EditorRoute
          content={content}
          onClose={() => { app.go({ kind: "menu" }); }}
        />
      );
    }

    if (snapshot.screen.kind === "newgame") {
      return (
        <NewGameScreen
          content={content}
          initial={lastSetup}
          onStart={start}
          onBack={() => { app.go({ kind: "menu" }); }}
        />
      );
    }

    if (snapshot.screen.kind === "adventure" && snapshot.session !== null) {
      return (
        <AdventureShell
          session={snapshot.session}
          uiAtlas={uiAtlas}
          pixiAtlas={assets.pixi}
          animations={snapshot.animationsOn}
          uiScale={settings.panelScale ?? undefined}
          onOpenMenu={openSystemPanel}
        />
      );
    }

    return (
      <MainMenu
        backdropFaction="sunspire"
        version={VERSION}
        items={menuItems}
        onSelect={onMenu}
      />
    );
  }, [ready, showsError, error, uiAtlas, snapshot, assets.pixi, start, menuItems, onMenu, settings.panelScale, openSystemPanel]);

  return (
    <AtlasContext.Provider value={uiAtlas ?? EMPTY_ATLAS}>
      <AudioProvider volumes={settings.volumes}>
      <GameTheme className="hm-app">
        {/* Renders nothing; it is where the music and the event cues live. */}
        <GameAudio snapshot={snapshot} session={session} inBattle={inBattle} />
        {/* The system layer's controls are built out of the screens' material
            kit, and every screen that uses it mounts the sheet itself. The
            adventure screen is not one of those, so a corner plate and a
            `ScreenDialog` drawn over the map came up as raw browser widgets -
            a stylesheet nothing mounts, which is this round's bug one layer
            down. Mounting it here is ref-counted and costs one <style> tag. */}
        <ScreenStyles />
        {body}
        <RotateHint />

        {panel !== null && panel !== "system" ? (
          <div className="hm-overlay" style={SYS.layer}>
            {panel === "settings" ? (
              <Settings
                values={settings}
                onChange={changeSettings}
                onClose={closePanel}
                hotkeys={SYSTEM_KEYS}
                extra={<OfflineCard />}
              />
            ) : null}
            {panel === "credits" ? (
              <CreditsScreen version={VERSION} onClose={closePanel} />
            ) : null}
            {panel === "scores" ? (
              <HighScores
                rows={scores}
                highlightAt={newestScoreAt}
                onClear={() => { clearScores(); setScores([]); setNewestScoreAt(null); }}
                onClose={closePanel}
              />
            ) : null}
            {panel === "save" || panel === "load" ? (
              <SaveLoad
                mode={panel}
                slots={slots}
                autosaveSlot={AUTOSAVE_SLOT}
                canSave={session !== null}
                day={session === null ? undefined : day}
                message={notice}
                onSave={doSave}
                onLoad={doLoad}
                onDelete={doDelete}
                onSaveToFile={doSaveFile}
                onLoadFromFile={doLoadFile}
                onClose={closePanel}
              />
            ) : null}
          </div>
        ) : null}

        {/* Over everything, including a dialog: the point of it is that the
            player who has just finished stops seeing the board, and a modal
            left open on top would be a hole in exactly that. */}
        {handoff !== null ? (
          <div className="hm-overlay" style={SYS.layer}>
            <HandOff
              colour={handoffColour}
              day={day}
              week={session?.getState().turn.week ?? 1}
              month={session?.getState().turn.month ?? 1}
              seats={handoffSeats}
              onReady={() => { app.takeSeat(); }}
            />
          </div>
        ) : null}

        {/* One dialog layer, ever.
            Each `ScreenDialog` brings its own full-frame `.hs-scrim`, so two of
            them mounted at once put one dialog's scrim and footer over the
            other's buttons - a modal you can see and cannot press. Which
            question is being asked is decided here, in one place, rather than
            by four independent conditionals that can all be true at once. */}
        {dialog !== null ? <div style={SYS.dialogLayer}>{dialog}</div> : null}

        {notice !== null && panel !== "save" && panel !== "load" ? (
          <div style={SYS.toast} role="status">
            <span>{notice}</span>
            <button
              type="button"
              className="hs-btn hs-btn--ghost"
              style={SYS.toastButton}
              onClick={() => { setNotice(null); }}
            >
              Dismiss
            </button>
          </div>
        ) : null}
      </GameTheme>
      </AudioProvider>
    </AtlasContext.Provider>
  );
}

/** Which question is on the screen, if any. Exactly one, by construction. */
function systemDialog(args: {
  readonly panel: Panel;
  readonly confirm: Confirm;
  readonly error: string | null;
  readonly slots: readonly SlotInfo[];
  readonly autosaveOn: boolean;
  readonly openPanel: (p: Panel) => void;
  readonly closePanel: () => void;
  readonly closeConfirm: () => void;
  readonly askLeave: () => void;
  readonly toMainMenu: () => void;
  readonly abandonGame: () => void;
  readonly closeTab: () => void;
  readonly dismissError: () => void;
}): JSX.Element | null {
  const { panel, confirm, error, slots } = args;
  const newest = slots[0];
  const newestText = newest === undefined
    ? ""
    : ` The newest save is "${newest.slot}", on day ${String(newest.day)}.`;

  // The error outranks everything: it is the one message that explains why the
  // thing underneath it may not be doing what the player asked.
  if (error !== null) {
    return (
      <ScreenDialog title="Something went wrong" width={480} onClose={args.dismissError}>
        <div className="hs-parch hs-why">{error}</div>
        <div className="hs-tiny hs-muted" style={{ marginTop: "8px" }}>
          Whatever was on screen is still behind this; closing this brings it back.
        </div>
      </ScreenDialog>
    );
  }

  if (confirm === "leave") {
    return (
      <ScreenDialog
        title="Go to the main menu?"
        width={440}
        onClose={args.closeConfirm}
        footer={
          <button type="button" className="hs-btn hs-btn--gold" onClick={args.toMainMenu}>
            Main Menu
          </button>
        }
      >
        <div className="hs-parch hs-why">
          The game stays where it is and Return to Game brings you straight back. It is
          only lost when you start another one, load another one, or abandon it from the
          title screen.{newestText}
        </div>
      </ScreenDialog>
    );
  }

  if (confirm === "abandon") {
    return (
      <ScreenDialog
        title="Abandon this game?"
        width={440}
        onClose={args.closeConfirm}
        footer={
          <button type="button" className="hs-btn hs-btn--gold" onClick={args.abandonGame}>
            Abandon
          </button>
        }
      >
        <div className="hs-parch hs-why">
          The game in progress is thrown away for good.
          {slots.length === 0
            ? " Nothing is saved in this browser, so there would be nothing to come back to."
            : `${newestText} It stays where it is.`}
        </div>
      </ScreenDialog>
    );
  }

  if (confirm === "close-tab") {
    return (
      <ScreenDialog
        title="Quit"
        width={440}
        onClose={args.closeConfirm}
        footer={
          <button type="button" className="hs-btn hs-btn--gold" onClick={args.closeTab}>
            Close Tab
          </button>
        }
      >
        <div className="hs-parch hs-why">
          There is no desktop to quit to: this is a page. The tab can be asked to close
          itself, and a browser will refuse if it did not open the tab in the first
          place - in which case nothing will happen and you can close it yourself.
        </div>
      </ScreenDialog>
    );
  }

  if (panel === "system") {
    return (
      <ScreenDialog title="Game Menu" width={360} onClose={args.closePanel}>
        <div style={SYS.stack}>
          <button type="button" className="hs-btn hs-btn--gold" onClick={() => { args.openPanel("save"); }}>
            Save Game
          </button>
          <button type="button" className="hs-btn" onClick={() => { args.openPanel("load"); }}>
            Load Game
          </button>
          <button type="button" className="hs-btn" onClick={() => { args.openPanel("settings"); }}>
            Settings
          </button>
          <button type="button" className="hs-btn" onClick={args.askLeave}>
            Main Menu
          </button>
        </div>
        <div className="hs-tiny hs-muted" style={{ marginTop: "10px" }}>
          {args.autosaveOn
            ? "The autosave is rewritten at the top of every one of your turns."
            : "Autosave is off - nothing is being written for you."}
        </div>
      </ScreenDialog>
    );
  }

  return null;
}

/* The system layer's own chrome. It is styled here rather than in global.css
   because it belongs to this component and to nothing else, and because the
   z-order is the point: the panels have to sit over the battle screen (20) and
   over the kernel's own prompt dialog (30), and the corner plate has to sit
   under both so a town screen covers it. */
const SYS: Readonly<Record<string, CSSProperties>> = {
  layer: { zIndex: 40 },
  dialogLayer: { position: "absolute", inset: 0, zIndex: 41 },
  stack: { display: "flex", flexDirection: "column", gap: 8 },
  toast: {
    position: "absolute", left: "50%", top: 14, zIndex: 42,
    transform: "translateX(-50%)",
    // A status line is not a control surface. It sits above the dialog layer so
    // it is legible over a modal, which is exactly why it must not be able to
    // swallow a click meant for the modal underneath.
    pointerEvents: "none",
    display: "flex", alignItems: "center", gap: 12, maxWidth: "72%",
    padding: "7px 12px",
    color: "#f0e0b6", background: "rgba(20, 14, 8, 0.94)",
    border: "2px solid #7a5c2e",
    font: "13px/1.4 Georgia, serif",
  },
  toastButton: { pointerEvents: "auto" },
};

// ---------------------------------------------------------------- fixtures

function Fixture(props: {
  readonly content: ContentRegistry;
  readonly pixi: Atlas | null;
  readonly fixture: NonNullable<ReturnType<typeof app.getSnapshot>["fixture"]>;
  readonly animations: boolean;
}): JSX.Element {
  const { content, fixture } = props;
  const close = useCallback(() => { app.showFixture(null); }, []);

  if (fixture.kind === "town") {
    const faction = fixture.faction ?? "sunspire";
    const town = townFixture(content, faction);
    const garrison = armyFixture(content, faction);
    const visitingArmy = armyFixture(content, faction, "army:2");
    return (
      <TownScreen
        content={content}
        town={town}
        treasury={{ gold: 41250, wood: 34, ore: 29, mercury: 12, sulfur: 11, crystal: 9, gems: 14 }}
        garrison={garrison}
        visitingHero={heroFixture(content, faction)}
        visitingArmy={visitingArmy}
        // There is no kernel state behind a fixture, so the pools are the
        // fixture's own numbers and every upgrade is quoted by the kernel's
        // price function with everything permitted, at a town's full price.
        available={(c) => town.available[c as string] ?? 0}
        upgradeOffers={(id) => {
          const army = id === garrison.id ? garrison : id === visitingArmy.id ? visitingArmy : null;
          return army === null ? [] : upgradeOffers(content, army.slots, () => true, () => FULL_PRICE_BP);
        }}
        day={4}
        tavernOffers={[]}
        buildReason={() => null}
        recruitReason={() => null}
        hireReason={() => null}
        ownerColour="red"
        onCommand={() => { /* a fixture accepts nothing */ }}
        onClose={close}
      />
    );
  }

  if (fixture.kind === "hero") {
    // A meeting needs a second hero, and the fixture's hero is deterministic,
    // so the partner is the same hero of another faction: different portrait,
    // different name, same shape.
    const meeting = fixture.meet === true
      ? heroFixture(content, fixture.faction === "greenmantle" ? "sunspire" : "greenmantle")
      : null;
    const hero = heroFixture(content, fixture.faction ?? "sunspire");
    return (
      <HeroScreen
        content={content}
        hero={hero}
        // Fixture data: nothing to resolve against, so the sheet shows the
        // fixture's stored primaries and its current mana as its own ceiling.
        primary={hero.primary}
        manaMax={hero.mana}
        army={armyFixture(content, fixture.faction ?? "sunspire", "army:2")}
        otherArmy={meeting === null ? null
          // "army:1" rather than another full seven: a meeting with both
          // armies full cannot demonstrate the send-across button at all, and
          // a fixture that only ever shows a greyed control is not a fixture
          // of the control.
          : armyFixture(content, fixture.faction === "greenmantle" ? "sunspire" : "greenmantle", "army:1")}
        otherLabel={meeting === null ? undefined
          : `${content.heroes.get(meeting.def)?.name ?? (meeting.def as string)}'s army`}
        otherHero={meeting}
        giveReason={() => null}
        checkCommand={() => null}
        onSwapHeroes={meeting === null ? undefined : () => { /* fixture */ }}
        ownerColour="blue"
        onCommand={() => { /* fixture */ }}
        onOpenSpellbook={() => { app.showFixture({ kind: "spellbook" }); }}
        onClose={close}
      />
    );
  }

  if (fixture.kind === "spellbook") {
    return (
      <SpellBook
        content={content}
        hero={heroFixture(content, fixture.faction ?? "sunspire")}
        onCast={() => { /* fixture */ }}
        onClose={close}
      />
    );
  }

  if (fixture.kind === "battle" && props.pixi !== null) {
    const siege = fixture.battle?.siege === true;
    const terrain = fixture.battle?.terrain;
    const attacker = fixture.battle?.attacker;
    return (
      <BattleFixture
        // The terrain is part of the identity, not just a prop: `battleFixture`
        // runs once in a `useState` initialiser, so without it in the key a
        // second scene on another arena would reuse the first one's board.
        key={`${siege ? "siege" : "field"}:${terrain ?? "default"}:${attacker ?? "sunspire"}`}
        content={content}
        atlas={props.pixi}
        siege={siege}
        {...(terrain === undefined ? {} : { terrain })}
        {...(attacker === undefined ? {} : { attacker })}
        animations={props.animations}
      />
    );
  }

  return <div className="hm-boot">Preparing scene…</div>;
}

/**
 * The debug battle, played for real.
 *
 * This is the scene seven rounds of art review looked at, and its `onCommand`
 * was an empty function - so a critic could study it for an hour without ever
 * discovering that a click did nothing. Reviewing a scene nobody can interact
 * with is how "units will not move in battle" survived every gate we have, so
 * the fixture now runs the kernel against a scratch `BattleState` of its own.
 *
 * It hands the viewer whichever side is to move, because a fixture has no
 * opponent to take the other bench; that makes the whole fight playable from
 * both sides, which is what a reviewer needs in order to review it. Nothing
 * here touches the session or the turn loop: a fixture still owns no game.
 *
 * `executeBattleCommand` mutates the state in place, so the version counter -
 * not the object identity - is what tells the screen that a frame changed.
 */
function BattleFixture(props: {
  readonly content: ContentRegistry;
  readonly atlas: Atlas;
  readonly siege: boolean;
  readonly terrain?: string;
  readonly attacker?: string;
  readonly animations: boolean;
}): JSX.Element {
  const { content, siege, terrain, attacker } = props;
  const opts = {
    siege,
    ...(terrain === undefined ? {} : { terrain }),
    ...(attacker === undefined ? {} : { attacker }),
  };
  const [built, setBuilt] = useState(() => battleFixture(content, opts));
  const [version, setVersion] = useState(1);
  const [refusal, setRefusal] = useState<string | null>(null);
  /**
   * The last command's events, so the console narrates.
   *
   * The fixture had `events={[]}` hard-coded, which meant the one scene seven
   * rounds of art review photographed showed the crimson recess with nothing in
   * it but "Round 1" - the strip that carries "The Ballista does 4 damage." in
   * the reference. `onCommand` has held `r.value.events` all along.
   */
  const [events, setEvents] = useState<readonly GameEvent[]>([]);
  const presenterRef = useRef<BattlePresenter | null>(null);

  const state = built.state;
  const slot = activeStack(state);
  const side: 0 | 1 = slot >= 0 && state.stacks.side[slot] === 1 ? 1 : 0;

  const onCommand = useCallback((cmd: BattleCommand): void => {
    const r = executeBattleCommand(state, cmd, content);
    // Illegality is returned, never thrown, and a fixture is exactly the place
    // where a refusal should be legible rather than swallowed.
    setRefusal(r.ok
      ? null
      : r.error.kind === "ILLEGAL" ? reasonText(r.error.reason) : r.error.issues.join("; "));
    setVersion((v) => v + 1);
    setEvents(r.ok ? r.value.events : []);
    if (r.ok) void presenterRef.current?.animate(r.value.events);
  }, [state, content]);

  const restart = useCallback((): void => {
    setBuilt(battleFixture(content, terrain === undefined ? { siege } : { siege, terrain }));
    setVersion((v) => v + 1);
    setRefusal(null);
    setEvents([]);
  }, [content, siege, terrain]);

  /**
   * Who is commanding each bench.
   *
   * A fixture owns no `GameState`, so there is no `HeroId` to resolve and the
   * console fell back to its faction stand-in - which is faction-correct and a
   * different person on each side of every shot. Naming them here is not a
   * lookup either, but it is the fixture's OWN choice rather than the
   * renderer's guess, it is the same pair in every photograph, and the two
   * sides come from their own rosters (`sunspire` against `gravewatch`, or
   * `greenmantle` under siege) instead of both from a hash.
   */
  const commanders = useMemo((): readonly [
    { readonly name: string; readonly portrait: string } | null,
    { readonly name: string; readonly portrait: string } | null,
  ] => {
    const of = (faction: string): { readonly name: string; readonly portrait: string } | null => {
      const roster = [...content.heroes.values()]
        .filter((d) => (d.faction as string) === faction)
        .sort((a, b) => ((a.id as string) < (b.id as string) ? -1 : 1));
      const def = roster[0];
      return def === undefined
        ? null
        : { name: def.name, portrait: heroPortrait(def.portrait) ?? "" };
    };
    return [of(built.attacker), of(built.defender)];
  }, [content, built]);

  const outcome = state.outcome.kind;
  return (
    <div className="hm-battle-fixture" style={FIXTURE.host}>
      <BattleScreen
        atlas={props.atlas}
        content={content}
        state={state}
        version={version}
        events={events}
        commanders={commanders}
        side={side}
        animations={props.animations}
        presenterRef={presenterRef}
        onCommand={onCommand}
      />
      {refusal !== null || outcome !== "ongoing" ? (
        <div className="hm-battle-fixture__toast" style={FIXTURE.toast}>
          <span>{outcome === "ongoing" ? refusal : `The battle is over: ${outcome}.`}</span>
          <button type="button" style={FIXTURE.button} onClick={restart}>Restart</button>
        </div>
      ) : null}
    </div>
  );
}

/* The fixture's own chrome, styled here rather than in global.css: it belongs
   to the debug scene and to nothing else, and it is drawn only when there is
   something to say, so the resting frame a critic photographs is unchanged. */
const FIXTURE: Readonly<Record<string, CSSProperties>> = {
  host: { position: "relative", width: "100%", height: "100%" },
  toast: {
    position: "absolute", left: "50%", top: 12, transform: "translateX(-50%)", zIndex: 3,
    display: "flex", alignItems: "center", gap: 12, padding: "7px 12px",
    color: "#f0e0b6", background: "rgba(20, 14, 8, 0.94)",
    border: "2px solid #7a5c2e", borderRadius: 3,
    font: "600 12px/1.2 ui-sans-serif, system-ui, sans-serif", letterSpacing: "0.06em",
  },
  button: {
    padding: "4px 10px", cursor: "pointer", color: "#f0e0b6",
    background: "linear-gradient(#4a3925, #2a1e12)",
    border: "2px solid #7a5c2e", borderRadius: 3, font: "inherit",
    textTransform: "uppercase", letterSpacing: "0.08em",
  },
};

export { BattleResult };
